/**
 * Role Autocomplete Hook
 * 
 * Provides autocomplete functionality for role search using Elasticsearch.
 * Supports debounced search with minimum 2 characters and popular items on empty query.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APIRoleAutocompleteResponse } from '../../../generated-types/models/APIRoleAutocompleteResponse'
import type { V1SearchAccountsRolesAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

interface RoleAutocompleteResponse {
  suggestions: Array<{
    id: string
    name: string
    description?: string
    sub_role_names: string[]
    self: string
  }>
  total: number
}

/**
 * Fetches role autocomplete suggestions
 */
const fetchRoleAutocomplete = async (
  query: string,
  size = 10
): Promise<RoleAutocompleteResponse> => {
  const response: APIRoleAutocompleteResponse = await v1Api.v1SearchAccountsRolesAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchAccountsRolesAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.name,
      description: suggestion.description,
      sub_role_names: suggestion.subRoleNames,
      self: `${baseUrl}v1/accounts/roles/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for role autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 */
export function useRoleAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<RoleAutocompleteResponse, Error>({
    queryKey: ['role-autocomplete', debouncedQuery],
    queryFn: () => fetchRoleAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


